import React, { useCallback, useEffect } from 'react'
import * as Yup from 'yup'
import { useStyles } from './styles'
import SimpleInput from 'components/common/SimpleInput/SimpleInput'
import { THEME_DARK } from 'constants.js'
import { useSelector, useDispatch } from 'react-redux'
import { AccessCodeRedemptionStatus, getAccessCode } from 'redux/slices/accessCode'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { validateAccessCode } from 'redux/slices/accessCode/thunks'
import CallToActionButton from 'components/common/CallToActionButton/CallToActionButton'
import { useAuth0, useAuthenticatedState, LoginAction } from 'libs/web-client/utils/auth0/src'

interface AccessCodeFormProps {
    cover: string
    showId: string
    title: string
}

type Inputs = {
    displayName: string
    email: string
    accessCode: string
    password: string
}

const accessCodeValidationSchema = Yup.object({
    accessCode: Yup.string().required('Please, type your unique access code'),
})

const AccessCodeForm: React.FC<AccessCodeFormProps> = React.memo(({ cover, title, showId }) => {
    const { loginWithPopup } = useAuth0()
    const { isAuthenticated, user } = useAuthenticatedState()
    const handleLoginClick = useCallback(() => loginWithPopup({ action: LoginAction.LOGIN }), [loginWithPopup])
    const onSignUpSubmit = useCallback(() => loginWithPopup({ action: LoginAction.REGISTER }), [loginWithPopup])

    const { errorMessage, status, currentCode } = useSelector(getAccessCode)
    const dispatch = useDispatch()
    const classes = useStyles({ cover })
    const onSubmit = useCallback(
        data => {
            if (isAuthenticated) {
                dispatch(validateAccessCode({ ...data, ...user, showId }))
            } else {
                console.error('<AccessCodeForm />: Access code onSubmit() handler called, but user not authenticated!')
            }
        },
        [isAuthenticated, showId, dispatch, user],
    )

    const { register, handleSubmit, errors, setValue } = useForm<Inputs>({
        resolver: yupResolver(accessCodeValidationSchema),
        mode: 'onBlur',
    })

    useEffect(() => {
        setValue('accessCode', currentCode)
    }, [currentCode, setValue])

    return (
        <div className={classes.container}>
            <div className={classes.formContainer}>
                <div className={classes.title}>
                    <h2>WELCOME TO</h2>
                    <h1>{title}</h1>
                </div>
                {isAuthenticated ? (
                    <form onSubmit={handleSubmit(onSubmit)} noValidate>
                        <SimpleInput
                            className={classes.input}
                            error={errors?.accessCode?.message}
                            themeColor={THEME_DARK}
                            type="text"
                            enableErrorIcon
                            placeholder="Enter your unique access code"
                            variant="secondary"
                            name="accessCode"
                            ref={register}
                        />

                        <input type="submit" value="Join Event" className={classes.submitBtn} />

                        {status === AccessCodeRedemptionStatus.ERROR && (
                            <div className={classes.error}>
                                <FontAwesomeIcon className={classes.errorIcon} icon={faExclamationTriangle} />
                                {errorMessage}
                            </div>
                        )}
                    </form>
                ) : (
                    <>
                        <CallToActionButton
                            onClick={onSignUpSubmit}
                            text="sign up for an account"
                            display="block"
                            type="secondary"
                            className={classes.signUpButton}
                            id="paywall-sign-up-submit-btn"
                        />

                        <p className={classes.paragraph}>
                            Already have an account?
                            <button className={classes.signInLink} onClick={handleLoginClick}>
                                Sign in to your account
                            </button>
                        </p>
                    </>
                )}
            </div>
        </div>
    )
})

export default React.memo(AccessCodeForm)
